// web/src/stores/conversationStore.ts
// Per-agent conversation threads. Each agent_id has its own message array.
// Luna is special: it supports multiple named conversation threads (lunaThreads)
// because users interact with Luna across many sessions and topics.
import { create } from "zustand";
import { api } from "../lib/apiClient";
function makeThread(agentId) {
    return {
        agentId,
        conversationId: null,
        messages: [],
        isTyping: false,
        taskId: null,
        turnNumber: 0,
    };
}
function generateId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
function lunaTitle(thread) {
    const first = thread.messages.find((m) => m.role === "user");
    if (!first)
        return "New conversation";
    return first.content.length > 120
        ? first.content.slice(0, 120) + "…"
        : first.content;
}
// Resolve the thread for a given agentId. For Luna, returns the active thread.
// For other agents, returns the thread keyed by agentId.
function resolveThread(agentId, threads, lunaThreads, activeLunaKey) {
    if (agentId === "luna") {
        if (activeLunaKey && lunaThreads[activeLunaKey]) {
            return lunaThreads[activeLunaKey];
        }
        return makeThread("luna");
    }
    return threads[agentId] ?? makeThread(agentId);
}
export const useConversationStore = create()((set, get) => ({
    threads: {},
    lunaThreads: {},
    activeLunaKey: null,
    isLoadingHistory: false,
    isLoadingArchive: false,
    hasMoreArchive: true,
    archiveOffset: 0,
    getThread: (agentId) => {
        const s = get();
        return resolveThread(agentId, s.threads, s.lunaThreads, s.activeLunaKey);
    },
    getActiveLunaThread: () => {
        const { lunaThreads, activeLunaKey } = get();
        if (activeLunaKey && lunaThreads[activeLunaKey]) {
            return lunaThreads[activeLunaKey];
        }
        return makeThread("luna");
    },
    getLunaConversationList: () => {
        const { lunaThreads } = get();
        return Object.entries(lunaThreads)
            .map(([key, thread]) => ({
            key,
            title: lunaTitle(thread),
            startedAt: thread.messages[0]?.timestamp ?? new Date().toISOString(),
        }))
            .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    },
    loadMoreLunaHistory: async () => {
        const s = get();
        if (s.isLoadingArchive || !s.hasMoreArchive)
            return;
        set({ isLoadingArchive: true });
        const batchSize = 20;
        try {
            const data = await api.getRecentConversations(batchSize, s.archiveOffset);
            if (!data.conversations.length) {
                set({ hasMoreArchive: false });
                return;
            }
            const lunaMap = {};
            for (const conv of data.conversations) {
                const agentMessages = conv.messages.filter((m) => m.role === "assistant");
                const primaryAgent = agentMessages[0]?.agent_id ?? "luna";
                if (primaryAgent !== "luna")
                    continue;
                const messages = conv.messages.map((m) => ({
                    id: m.id,
                    role: m.role,
                    content: m.content,
                    agent_id: m.agent_id !== "user" ? m.agent_id : undefined,
                    renders: m.renders,
                    specialists: m.specialists,
                    response_template: m.response_template,
                    executive_summary: m.executive_summary,
                    headline_metrics: m.headline_metrics,
                    analysis_narrative: m.analysis_narrative,
                    inline_renders: m.inline_renders,
                    executive_situation: m.executive_situation,
                    executive_recommendation: m.executive_recommendation,
                    situation_narrative: m.situation_narrative,
                    balance_sheet: m.balance_sheet,
                    cash_flow: m.cash_flow,
                    assumptions: m.assumptions,
                    assumptions_used: m.assumptions_used,
                    risks: m.risks,
                    open_items: m.open_items,
                    unified_recommendations: m.unified_recommendations,
                    pending: m.pending,
                    task_id: m.task_id,
                    timestamp: m.timestamp,
                }));
                lunaMap[conv.conversation_id] = {
                    agentId: "luna",
                    conversationId: conv.conversation_id,
                    messages,
                    isTyping: false,
                    taskId: null,
                    turnNumber: conv.message_count,
                };
            }
            set((prev) => ({
                lunaThreads: { ...prev.lunaThreads, ...lunaMap },
                archiveOffset: prev.archiveOffset + data.conversations.length,
                hasMoreArchive: data.conversations.length >= batchSize,
            }));
        }
        catch {
            // Non-fatal — archive load failures degrade gracefully
        }
        finally {
            set({ isLoadingArchive: false });
        }
    },
    loadHistory: async () => {
        if (get().isLoadingHistory)
            return;
        set({ isLoadingHistory: true });
        try {
            // Fetch more conversations so Luna has a full list to show
            const data = await api.getRecentConversations(10);
            if (!data.conversations.length)
                return;
            const threadMap = {};
            const lunaMap = {};
            let firstLunaKey = null;
            for (const conv of data.conversations) {
                const agentMessages = conv.messages.filter((m) => m.role === "assistant");
                const primaryAgent = agentMessages[0]?.agent_id ?? "luna";
                const messages = conv.messages.map((m) => ({
                    id: m.id,
                    role: m.role,
                    content: m.content,
                    agent_id: m.agent_id !== "user" ? m.agent_id : undefined,
                    renders: m.renders,
                    specialists: m.specialists,
                    response_template: m.response_template,
                    executive_summary: m.executive_summary,
                    headline_metrics: m.headline_metrics,
                    analysis_narrative: m.analysis_narrative,
                    inline_renders: m.inline_renders,
                    executive_situation: m.executive_situation,
                    executive_recommendation: m.executive_recommendation,
                    situation_narrative: m.situation_narrative,
                    balance_sheet: m.balance_sheet,
                    cash_flow: m.cash_flow,
                    assumptions: m.assumptions,
                    assumptions_used: m.assumptions_used,
                    risks: m.risks,
                    open_items: m.open_items,
                    unified_recommendations: m.unified_recommendations,
                    // Refresh-resilience: surface in-flight assistant rows so the
                    // UI renders the pending placeholder and the WS turn_complete
                    // handler finalizes it.
                    pending: m.pending,
                    task_id: m.task_id,
                    timestamp: m.timestamp,
                }));
                // If the most recent assistant message is still pending, bind
                // the thread to its task_id so WS pipeline events attach to
                // the placeholder after a refresh.
                const lastPending = [...messages]
                    .reverse()
                    .find((m) => m.role === "assistant" && m.pending);
                const thread = {
                    agentId: primaryAgent,
                    conversationId: conv.conversation_id,
                    messages,
                    isTyping: false,
                    taskId: lastPending?.task_id ?? null,
                    turnNumber: conv.message_count,
                };
                if (primaryAgent === "luna") {
                    // Use the backend conversation_id as the key for history-loaded threads
                    const key = conv.conversation_id;
                    lunaMap[key] = thread;
                    if (!firstLunaKey)
                        firstLunaKey = key;
                }
                else {
                    // One thread per non-luna agent — keep the most recent
                    if (!threadMap[primaryAgent]) {
                        threadMap[primaryAgent] = thread;
                    }
                }
            }
            set((s) => {
                // Merge non-luna threads
                const merged = { ...s.threads };
                for (const [agentId, thread] of Object.entries(threadMap)) {
                    if (!merged[agentId] || merged[agentId].messages.length === 0) {
                        merged[agentId] = thread;
                    }
                }
                // Merge luna threads — only add history entries we don't already have
                const mergedLuna = { ...lunaMap, ...s.lunaThreads };
                // Set active luna key to most recent if not already set
                const newActiveLunaKey = s.activeLunaKey && mergedLuna[s.activeLunaKey]
                    ? s.activeLunaKey
                    : firstLunaKey ?? s.activeLunaKey;
                return {
                    threads: merged,
                    lunaThreads: mergedLuna,
                    activeLunaKey: newActiveLunaKey,
                    archiveOffset: data.conversations.length,
                };
            });
        }
        catch {
            // Non-fatal — history load failures degrade gracefully
        }
        finally {
            set({ isLoadingHistory: false });
        }
    },
    addUserMessage: (agentId, content) => {
        const id = generateId();
        const msg = {
            id,
            role: "user",
            content,
            timestamp: new Date().toISOString(),
        };
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey ?? generateId();
                const existing = s.lunaThreads[key] ?? makeThread("luna");
                return {
                    lunaThreads: {
                        ...s.lunaThreads,
                        [key]: {
                            ...existing,
                            messages: [...existing.messages, msg],
                            turnNumber: existing.turnNumber + 1,
                        },
                    },
                    activeLunaKey: key,
                };
            }
            const existing = s.threads[agentId] ?? makeThread(agentId);
            return {
                threads: {
                    ...s.threads,
                    [agentId]: {
                        ...existing,
                        messages: [...existing.messages, msg],
                        turnNumber: existing.turnNumber + 1,
                    },
                },
            };
        });
        return id;
    },
    addPendingAssistantMessage: (agentId, taskId) => {
        const id = generateId();
        const msg = {
            id,
            role: "assistant",
            content: "",
            agent_id: agentId,
            task_id: taskId,
            pending: true,
            timestamp: new Date().toISOString(),
        };
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey ?? generateId();
                const existing = s.lunaThreads[key] ?? makeThread("luna");
                return {
                    lunaThreads: {
                        ...s.lunaThreads,
                        [key]: { ...existing, messages: [...existing.messages, msg] },
                    },
                    activeLunaKey: key,
                };
            }
            const existing = s.threads[agentId] ?? makeThread(agentId);
            return {
                threads: {
                    ...s.threads,
                    [agentId]: { ...existing, messages: [...existing.messages, msg] },
                },
            };
        });
        return id;
    },
    addAssistantMessage: (agentId, partial) => {
        const id = generateId();
        const newMsg = {
            ...partial,
            id,
            timestamp: new Date().toISOString(),
        };
        // Replace a pending placeholder if one exists for this taskId.
        // Falls back to append when no placeholder is found (e.g. error
        // path that didn't pre-create one).
        const placeholderTaskId = partial.task_id;
        function spliceOrAppend(messages) {
            if (!placeholderTaskId)
                return [...messages, newMsg];
            // Idempotent by task_id — match any assistant message bound to
            // this task (pending OR already-replaced). Two independent
            // completion paths race on the same turn (in-page pollResult +
            // turn_complete WS handler for refresh recovery); whichever
            // arrives second must REPLACE, never append.
            const idx = messages.findIndex((m) => m.role === "assistant" && m.task_id === placeholderTaskId);
            if (idx < 0)
                return [...messages, newMsg];
            // Preserve the placeholder's id + accumulated pipeline_steps so the
            // accordion stays open mid-render without flicker. Force-finalize
            // any steps still marked pending/running — the response is here, so
            // by definition no step is still in flight. Without this, the live
            // pipeline accordion (isLive = any step running) stays open with a
            // spinner forever even after the message is done.
            const finalizedSteps = (messages[idx].pipeline_steps ?? newMsg.pipeline_steps ?? []).map((s) => s.status === "pending" || s.status === "running"
                ? { ...s, status: "done" }
                : s);
            // Prefer the server-supplied trace_steps if richer (have llm_input/
            // llm_output detail panels). The placeholder's steps are summary-only.
            const incomingSteps = newMsg.pipeline_steps ?? [];
            const chosenSteps = incomingSteps.length >= finalizedSteps.length
                ? incomingSteps
                : finalizedSteps;
            // The result message is being spliced in — by definition no step is
            // still in flight. Force-finalize whichever list we picked so the
            // pipeline accordion collapses and stops spinning.
            const useSteps = chosenSteps.map((s) => s.status === "pending" || s.status === "running"
                ? { ...s, status: "done" }
                : s);
            const preserved = {
                ...newMsg,
                id: messages[idx].id,
                pipeline_steps: useSteps,
                pending: false,
            };
            const copy = messages.slice();
            copy[idx] = preserved;
            return copy;
        }
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey ?? generateId();
                const existing = s.lunaThreads[key] ?? makeThread("luna");
                return {
                    lunaThreads: {
                        ...s.lunaThreads,
                        [key]: {
                            ...existing,
                            messages: spliceOrAppend(existing.messages),
                            isTyping: false,
                            taskId: null,
                        },
                    },
                };
            }
            const existing = s.threads[agentId] ?? makeThread(agentId);
            return {
                threads: {
                    ...s.threads,
                    [agentId]: {
                        ...existing,
                        messages: spliceOrAppend(existing.messages),
                        isTyping: false,
                        taskId: null,
                    },
                },
            };
        });
        return id;
    },
    setTyping: (agentId, typing) => {
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey;
                if (!key)
                    return {};
                return {
                    lunaThreads: {
                        ...s.lunaThreads,
                        [key]: { ...(s.lunaThreads[key] ?? makeThread("luna")), isTyping: typing },
                    },
                };
            }
            return {
                threads: {
                    ...s.threads,
                    [agentId]: {
                        ...(s.threads[agentId] ?? makeThread(agentId)),
                        isTyping: typing,
                    },
                },
            };
        });
    },
    setTaskId: (agentId, taskId) => {
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey;
                if (!key)
                    return {};
                return {
                    lunaThreads: {
                        ...s.lunaThreads,
                        [key]: { ...(s.lunaThreads[key] ?? makeThread("luna")), taskId },
                    },
                };
            }
            return {
                threads: {
                    ...s.threads,
                    [agentId]: {
                        ...(s.threads[agentId] ?? makeThread(agentId)),
                        taskId,
                    },
                },
            };
        });
    },
    setConversationId: (agentId, convId) => {
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey;
                if (!key)
                    return {};
                const thread = s.lunaThreads[key] ?? makeThread("luna");
                // If the backend assigned a real UUID and the current key is a temp key,
                // re-key the thread under the real conversation_id.
                if (key !== convId) {
                    const updatedThread = { ...thread, conversationId: convId };
                    const updated = { ...s.lunaThreads };
                    delete updated[key];
                    updated[convId] = updatedThread;
                    return { lunaThreads: updated, activeLunaKey: convId };
                }
                return {
                    lunaThreads: {
                        ...s.lunaThreads,
                        [key]: { ...thread, conversationId: convId },
                    },
                };
            }
            return {
                threads: {
                    ...s.threads,
                    [agentId]: {
                        ...(s.threads[agentId] ?? makeThread(agentId)),
                        conversationId: convId,
                    },
                },
            };
        });
    },
    updatePipelineSteps: (agentId, steps, taskId) => {
        // Resolve the target message by taskId (preferred — keys directly
        // off the WS frame's task_id) and fall back to the last message
        // only when no taskId was provided (legacy callers).
        function patch(msgs) {
            if (msgs.length === 0)
                return msgs;
            let idx = -1;
            if (taskId) {
                idx = msgs.findIndex((m) => m.role === "assistant" && m.task_id === taskId);
            }
            if (idx < 0)
                idx = msgs.length - 1;
            if (idx < 0)
                return msgs;
            const target = msgs[idx];
            // Don't write pipeline_steps onto a user message — the old bug.
            if (target.role !== "assistant")
                return msgs;
            const copy = msgs.slice();
            copy[idx] = { ...target, pipeline_steps: steps };
            return copy;
        }
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey;
                if (!key)
                    return {};
                const thread = s.lunaThreads[key] ?? makeThread("luna");
                return {
                    lunaThreads: {
                        ...s.lunaThreads,
                        [key]: { ...thread, messages: patch(thread.messages) },
                    },
                };
            }
            const thread = s.threads[agentId] ?? makeThread(agentId);
            return {
                threads: {
                    ...s.threads,
                    [agentId]: { ...thread, messages: patch(thread.messages) },
                },
            };
        });
    },
    setMessageEta: (agentId, taskId, etaSeconds, etaN) => {
        function patch(msgs) {
            const idx = msgs.findIndex((m) => m.role === "assistant" && m.task_id === taskId);
            if (idx < 0)
                return msgs;
            const copy = msgs.slice();
            copy[idx] = { ...copy[idx], eta_seconds: etaSeconds, eta_n: etaN };
            return copy;
        }
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey;
                if (!key)
                    return {};
                const thread = s.lunaThreads[key] ?? makeThread("luna");
                return {
                    lunaThreads: {
                        ...s.lunaThreads,
                        [key]: { ...thread, messages: patch(thread.messages) },
                    },
                };
            }
            const thread = s.threads[agentId] ?? makeThread(agentId);
            return {
                threads: {
                    ...s.threads,
                    [agentId]: { ...thread, messages: patch(thread.messages) },
                },
            };
        });
    },
    clearThread: (agentId) => {
        set((s) => {
            if (agentId === "luna") {
                const key = s.activeLunaKey;
                if (!key)
                    return {};
                return {
                    lunaThreads: { ...s.lunaThreads, [key]: makeThread("luna") },
                };
            }
            return {
                threads: { ...s.threads, [agentId]: makeThread(agentId) },
            };
        });
    },
    createLunaConversation: () => {
        const key = generateId();
        set((s) => ({
            lunaThreads: { ...s.lunaThreads, [key]: makeThread("luna") },
            activeLunaKey: key,
        }));
        return key;
    },
    switchLunaConversation: (key) => {
        set({ activeLunaKey: key });
    },
    deleteLunaConversation: (key) => {
        // Grab the backend conversation_id before removing from state.
        const { lunaThreads } = get();
        const conversationId = lunaThreads[key]?.conversationId ?? null;
        // Optimistically remove from local state immediately.
        set((s) => {
            const updated = { ...s.lunaThreads };
            delete updated[key];
            const keys = Object.keys(updated);
            const newActive = s.activeLunaKey === key
                ? keys.length > 0 ? keys[0] : null
                : s.activeLunaKey;
            return { lunaThreads: updated, activeLunaKey: newActive };
        });
        // Fire-and-forget backend delete if the conversation was persisted.
        if (conversationId) {
            import("../lib/apiClient").then(({ api }) => {
                api.deleteConversation(conversationId).catch(() => {
                    // Silently ignore — local state is already cleaned up.
                });
            });
        }
    },
}));
